'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/StatusBadge';
import { Search } from 'lucide-react';

const installs = [
  { id: 'ai-001', customer: 'Rodriguez, A.', address: '4821 Barranca Pkwy, Irvine', system: 'Carrier Infinity 5-ton', value: '$21,400', schedDate: 'Jul 2', permit: 'Approved', equipment: 'Delivered', crew: 'Torres + Santos', inspection: 'Jul 8', status: 'Ready' },
  { id: 'ai-002', customer: 'Delgado, M.', address: '18 Shady Canyon, Irvine', system: 'Lennox XC21 5-ton', value: '$18,400', schedDate: 'Jul 3', permit: 'Approved', equipment: 'Warehouse', crew: 'Reyes + Kim', inspection: 'Jul 9', status: 'Ready' },
  { id: 'ai-003', customer: 'Yamamoto, K.', address: '2200 Newport Blvd, CM', system: 'Trane XV20i 4-ton', value: '$16,800', schedDate: 'Jul 3', permit: 'Correction Req.', equipment: 'Delivered', crew: 'Park + Williams', inspection: 'TBD', status: 'Blocked' },
  { id: 'ai-004', customer: 'Kim, S.', address: '9900 Alton Pkwy, Irvine', system: 'York YZV 3-ton', value: '$14,200', schedDate: 'Jul 4', permit: 'Approved', equipment: 'In Transit', crew: 'Patel + Chen', inspection: 'Jul 10', status: 'On Track' },
  { id: 'ai-005', customer: 'Garcia, M.', address: '1100 Edinger Ave, Tustin', system: 'Rheem Prestige 5-ton', value: '$19,800', schedDate: 'Jul 5', permit: 'Approved', equipment: 'Warehouse', crew: 'Torres + Reyes', inspection: 'Jul 11', status: 'On Track' },
  { id: 'ai-006', customer: 'Thompson, W.', address: '55 Monarch Beach, Dana Pt', system: 'Carrier 24ACC648 5-ton', value: '$22,100', schedDate: 'Jul 7', permit: 'In Review', equipment: 'Ordered', crew: 'Park + Santos', inspection: 'TBD', status: 'Delayed' },
  { id: 'ai-007', customer: 'Lee, J.', address: '7700 Beach Blvd, Buena Park', system: 'Lennox SL18XC1 4-ton', value: '$15,600', schedDate: 'Jul 7', permit: 'Approved', equipment: 'Delivered', crew: 'Kim + Williams', inspection: 'Jul 13', status: 'Ready' },
  { id: 'ai-008', customer: 'Wilson, C.', address: '3300 Michelson Dr, Irvine', system: 'Trane XR15 3-ton', value: '$12,400', schedDate: 'Jul 8', permit: 'Submitted', equipment: 'Ordered', crew: 'Unassigned', inspection: 'TBD', status: 'Delayed' },
  { id: 'ai-009', customer: 'Martinez, L.', address: '8811 Garden Grove Blvd', system: 'York YZV 4-ton', value: '$17,200', schedDate: 'Jul 9', permit: 'Approved', equipment: 'Warehouse', crew: 'Chen + Patel', inspection: 'Jul 15', status: 'On Track' },
  { id: 'ai-010', customer: 'Anderson, P.', address: '400 Columbia, Aliso Viejo', system: 'Carrier 24ACC636 5-ton', value: '$20,800', schedDate: 'Jul 10', permit: 'Submitted', equipment: 'Ordered', crew: 'Unassigned', inspection: 'TBD', status: 'Pending' },
];

const statusVariant: Record<string, 'active' | 'complete' | 'warning' | 'error' | 'pending' | 'idle'> = {
  'Ready': 'complete',
  'On Track': 'active',
  'Blocked': 'error',
  'Delayed': 'warning',
  'Pending': 'pending',
};

export default function ActiveInstallsTable() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');

  const statuses = ['All', 'Ready', 'On Track', 'Delayed', 'Blocked', 'Pending'];

  const filtered = installs.filter(i =>
    (filter === 'All' || i.status === filter) &&
    i.customer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-card rounded-lg card-shadow border border-border">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Active Install Projects</h3>
          <p className="text-xs text-muted-foreground mt-0.5">18 installs · $287,400 backlog value</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer..."
              className="pl-8 pr-3 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring w-40"
            />
          </div>
          <div className="flex items-center gap-1">
            {statuses.map(s => (
              <button
                key={`install-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-xs px-2 py-1 rounded-md transition-colors ${filter === s ? 'bg-secondary text-white' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Customer</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">System</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Value</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Sched. Date</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Permit</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Equipment</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Crew</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Inspection</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inst) => (
              <tr key={inst.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-foreground whitespace-nowrap">{inst.customer}</div>
                  <div className="text-muted-foreground truncate max-w-[150px]">{inst.address}</div>
                </td>
                <td className="px-3 py-2.5 text-foreground whitespace-nowrap">{inst.system}</td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular text-foreground">{inst.value}</td>
                <td className="px-3 py-2.5 text-foreground font-tabular whitespace-nowrap">{inst.schedDate}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs font-medium ${inst.permit === 'Approved' ? 'text-success' : inst.permit === 'Correction Req.' ? 'text-danger' : inst.permit === 'In Review' ? 'text-warning' : 'text-muted-foreground'}`}>{inst.permit}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs ${inst.equipment === 'Delivered' ? 'text-success' : inst.equipment === 'In Transit' ? 'text-warning' : 'text-muted-foreground'}`}>{inst.equipment}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs ${inst.crew === 'Unassigned' ? 'text-danger font-medium' : 'text-foreground'}`}>{inst.crew}</span>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground font-tabular whitespace-nowrap">{inst.inspection}</td>
                <td className="px-3 py-2.5">
                  <StatusBadge variant={statusVariant[inst.status] ?? 'idle'} label={inst.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Showing {filtered.length} of {installs.length} installs</span>
        <div className="flex items-center gap-4">
          <span>Ready: <span className="text-success font-semibold">3</span></span>
          <span>On Track: <span className="text-secondary font-semibold">3</span></span>
          <span>Delayed: <span className="text-warning font-semibold">2</span></span>
          <span>Blocked: <span className="text-danger font-semibold">1</span></span>
        </div>
      </div>
    </div>
  );
}